import { supabase } from './supabase';
import { databaseService } from './databaseService';
import { AuthServicesLogger, SupabaseLogger } from '../utils/logger';
import { userActions } from '../state/globalState';
import { APP_CONSTANTS } from '../config/constants';
import { Platform } from 'react-native';
import { getURL } from './authLinks';
import {
  captureSentryExceptionWithScope,
  captureSentryMessageWithScope,
} from './monitoring/sentry';

// Interfaces
interface UserCheckResult {
  exists: boolean;
  isLocal: boolean;
  userId: string;
}

type AuthErrorLike = {
  code?: unknown;
  status?: unknown;
  message?: unknown;
};

const normalizeLoginEmail = (email: string): string => email.trim().toLowerCase();

const getEmailDomain = (email: string): string => {
  const normalized = normalizeLoginEmail(email);
  const atIndex = normalized.lastIndexOf('@');
  if (atIndex <= 0 || atIndex >= normalized.length - 1) {
    return 'unknown';
  }
  return normalized.slice(atIndex + 1);
};

const getAuthErrorCode = (error: unknown): string => {
  const code = (error as AuthErrorLike | null | undefined)?.code;
  if (typeof code === 'string' && code.trim()) {
    return code.trim();
  }
  return 'unknown';
};

const getAuthErrorStatus = (error: unknown): string => {
  const status = (error as AuthErrorLike | null | undefined)?.status;
  if (typeof status === 'number' && Number.isFinite(status)) {
    return String(status);
  }
  if (typeof status === 'string' && status.trim()) {
    return status.trim();
  }
  return 'unknown';
};

const captureLoginFailure = ({
  email,
  password,
  error,
  source,
}: {
  email: string;
  password: string;
  error: unknown;
  source: string;
}): void => {
  const loginEmail = normalizeLoginEmail(email);
  const emailDomain = getEmailDomain(loginEmail);
  const authErrorCode = getAuthErrorCode(error);
  const authErrorStatus = getAuthErrorStatus(error);
  const baseContext = {
    flow: 'auth',
    stage: 'login',
    source,
    loginEmail,
    emailDomain,
    passwordLength: password.length,
    authErrorCode,
    authErrorStatus,
  };
  const tags = {
    flow: 'auth',
    stage: 'login',
    result: 'failure',
    login_email: loginEmail,
    email_domain: emailDomain,
    auth_error_code: authErrorCode,
    auth_status: authErrorStatus,
  };
  const fingerprint = ['auth', 'login_failure', authErrorCode, authErrorStatus];
  const dedupeKey = `auth:login_failure:${authErrorCode}:${authErrorStatus}:${loginEmail}`;

  if (error instanceof Error) {
    captureSentryExceptionWithScope(error, {
      level: 'error',
      context: baseContext,
      tags,
      fingerprint,
      dedupeKey,
      dedupeWindowMs: 30000,
    });
    return;
  }

  captureSentryMessageWithScope('Falha de login com erro não padronizado', {
    level: 'error',
    context: {
      ...baseContext,
      rawError: String(error),
    },
    tags,
    fingerprint,
    dedupeKey,
    dedupeWindowMs: 30000,
  });
};

/**
 * Realiza login com email e senha
 * @param email Email do usuário
 * @param password Senha do usuário
 */
export const loginWithEmail = async (email: string, password: string) => {
  AuthServicesLogger.Login.info(`Tentando login com email: ${email.substring(0, 3)}...`);

  try {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password
    });

    if (error) {
      captureLoginFailure({
        email,
        password,
        error,
        source: 'supabase.auth.signInWithPassword',
      });
      AuthServicesLogger.Login.warn('Erro no login:', error);
      return { data: null, error };
    }

    AuthServicesLogger.Login.info('Login realizado com sucesso');
    SupabaseLogger.logSessionState(data.session, 'Login');

    return { data, error: null };
  } catch (error) {
    captureLoginFailure({
      email,
      password,
      error,
      source: 'loginWithEmail.catch',
    });
    AuthServicesLogger.Login.warn('Exceção no login:', error);
    return { data: null, error };
  }
};

/**
 * Verifica se o usuário atual existe no banco de dados local e/ou remoto
 * @param userId ID do usuário para verificar
 * @returns Objeto com resultado da verificação
 */
export const checkUserExists = async (userId: string): Promise<UserCheckResult> => {
  AuthServicesLogger.UserCheck.info(`Verificando se usuário existe: ${userId}`);

  if (!userId) {
    AuthServicesLogger.UserCheck.warn('Tentativa de verificar usuário com ID vazio');
    return { exists: false, isLocal: false, userId: '' };
  }

  try {
    // Verificar no banco local primeiro
    AuthServicesLogger.UserCheck.info(`Verificando usuário ${userId} no banco de dados local...`);
    const localUser = await databaseService.readItem(APP_CONSTANTS.DATABASE.TABLES.USERS.NAME, userId);

    if (localUser) {
      AuthServicesLogger.UserCheck.info(`Usuário ${userId} encontrado no banco local`);
      return { exists: true, isLocal: true, userId };
    }

    // Se não encontrar localmente, verifica no Supabase
    AuthServicesLogger.UserCheck.info(`Usuário não encontrado localmente. Verificando no Supabase...`);
    const { data: remoteUsers, error } = await supabase
      .from(APP_CONSTANTS.DATABASE.TABLES.USERS.NAME)
      .select('*')
      .eq('id', userId)
      .limit(1);

    if (error) {
      AuthServicesLogger.UserCheck.error('Erro ao verificar usuário no Supabase:', error);
      return { exists: false, isLocal: false, userId };
    }

    AuthServicesLogger.UserCheck.info(`Resposta do Supabase para verificação de usuário:`, remoteUsers);

    const exists = Array.isArray(remoteUsers) && remoteUsers.length > 0;

    if (exists) {
      AuthServicesLogger.UserCheck.info(`Usuário ${userId} encontrado no Supabase. Dados:`, remoteUsers[0]);

      // Verificar se o app requer empresa
      if (APP_CONSTANTS.REGISTRATION.REQUIRES_COMPANY) {
        AuthServicesLogger.UserCheck.info('App requer empresa. Verificando empresa do usuário...');
        const userEmpresaId = remoteUsers[0].empresa_id;

        if (userEmpresaId) {
          AuthServicesLogger.UserCheck.info(`Encontrado empresa_id do usuário: ${userEmpresaId}. Buscando detalhes da empresa...`);

          // Buscar dados da empresa no Supabase
          const { data: empresaData, error: empresaError } = await supabase
            .from(APP_CONSTANTS.DATABASE.TABLES.EMPRESAS.NAME)
            .select('*')
            .eq('id', userEmpresaId)
            .limit(1);

          if (empresaError) {
            AuthServicesLogger.UserCheck.error(`Erro ao buscar dados da empresa ${userEmpresaId}:`, empresaError);
          } else if (empresaData && empresaData.length > 0) {
            AuthServicesLogger.UserCheck.info(`Empresa ${userEmpresaId} encontrada no Supabase. Dados:`, empresaData[0]);

            // Criar empresa no banco local primeiro
            try {
              await databaseService.createItem(APP_CONSTANTS.DATABASE.TABLES.EMPRESAS.NAME, empresaData[0], {
                skipPendingOperation: true,
                source: 'remote',
              });
              AuthServicesLogger.UserCheck.info(`Empresa ${userEmpresaId} criada no banco local com sucesso`);
            } catch (empresaError) {
              AuthServicesLogger.UserCheck.warn(`Erro ao criar empresa ${userEmpresaId} no banco local:`, empresaError);
              // Continuar mesmo com erro na criação da empresa
            }
          } else {
            AuthServicesLogger.UserCheck.warn(`Empresa ${userEmpresaId} não encontrada no Supabase, mas é requerida pelo app`);
          }
        } else {
          AuthServicesLogger.UserCheck.warn('Usuário não possui empresa_id associado, embora o app requeira empresa');
        }
      }

      // Após verificar/criar empresa, criar usuário no banco local
      try {
        await databaseService.createItem(APP_CONSTANTS.DATABASE.TABLES.USERS.NAME, remoteUsers[0], {
          skipPendingOperation: true,
          source: 'remote',
        });
        AuthServicesLogger.UserCheck.info(`Usuário ${userId} sincronizado para o banco local`);
      } catch (dbError) {
        AuthServicesLogger.UserCheck.warn('Erro ao sincronizar usuário para banco local:', dbError);
      }
    } else {
      AuthServicesLogger.UserCheck.info(`Usuário ${userId} não encontrado no Supabase. Resultado:`, remoteUsers);
    }

    return { exists, isLocal: false, userId };
  } catch (error) {
    AuthServicesLogger.UserCheck.error('Exceção ao verificar existência do usuário:', error);
    return { exists: false, isLocal: false, userId };
  }
};

/**
 * Faz logout do usuário
 */
export const logout = async () => {
  AuthServicesLogger.Main.info('Realizando logout');

  try {
    const { error } = await supabase.auth.signOut();

    if (error) {
      AuthServicesLogger.Main.error('Erro ao fazer logout:', error);
      // O SDK pode remover a sessão local mesmo quando a revogação remota falha sem rede.
      // Nesse caso, limpar também o usuário em memória permite trocar de conta sem tocar nas filas.
      try {
        const { data, error: sessionError } = await supabase.auth.getSession();
        if (!sessionError && !data.session) {
          AuthServicesLogger.Main.warn('Sessão local removida; revogação remota do logout não confirmada.');
          userActions.clearUser();
          return { success: true, error: null };
        }
      } catch (sessionCheckError) {
        AuthServicesLogger.Main.warn('Não foi possível confirmar a remoção da sessão local:', sessionCheckError);
      }
      return { success: false, error };
    }

    // Atualizar o estado global após logout bem-sucedido
    AuthServicesLogger.Main.info('Logout realizado com sucesso');
    userActions.clearUser();

    return { success: true, error: null };
  } catch (error) {
    AuthServicesLogger.Main.error('Exceção no logout:', error);
    return { success: false, error };
  }
};

/**
 * Utilitário para limpar todos os dados do banco local (apenas para testes)
 */
export const clearDatabaseForTesting = async (): Promise<boolean> => {
  AuthServicesLogger.Main.info('Limpando banco de dados para testes');

  try {
    const result = await databaseService.clearAllData();
    if (result) {
      AuthServicesLogger.Main.info('Banco de dados limpo com sucesso');
    } else {
      AuthServicesLogger.Main.warn('Falha ao limpar banco de dados');
    }
    return result;
  } catch (error) {
    AuthServicesLogger.Main.error('Erro ao limpar banco de dados:', error);
    return false;
  }
};

/**
 * Envia email de recuperação de senha para o usuário
 * @param email Email do usuário para recuperação
 * @returns Objeto com sucesso ou erro da operação
 */
export const sendPasswordRecoveryEmail = async (email: string) => {
  AuthServicesLogger.Main.info(`Solicitando recuperação de senha para: ${email.substring(0, 3)}...`);

  try {
    // Definir o redirecionamento baseado na plataforma (web ou mobile)
    let redirectTo = '';

    if (Platform.OS === 'web') {
      redirectTo = `${APP_CONSTANTS.URLS.PUBLIC_URL}${APP_CONSTANTS.URLS.RESET_PASSWORD_PATH}`;
    } else {
      // Para mobile, usa o scheme://reset-password
      const baseUrl = getURL();
      AuthServicesLogger.Main.info(`URL base para redirecionamento: ${baseUrl}`);
      redirectTo = `${baseUrl}reset-password`;
    }

    AuthServicesLogger.Main.debug(`URL de redirecionamento para reset: ${redirectTo}`);

    const { data, error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo
    });

    if (error) {
      AuthServicesLogger.Main.error('Erro ao solicitar recuperação de senha:', error);
      return { success: false, error };
    }

    AuthServicesLogger.Main.info('Email de recuperação enviado com sucesso');
    return { success: true, data, error: null };
  } catch (error) {
    AuthServicesLogger.Main.error('Exceção ao solicitar recuperação de senha:', error);
    return { success: false, error };
  }
};

/**
 * Atualiza a senha do usuário após recuperação
 * @param newPassword Nova senha a ser definida
 */
export const updateUserPassword = async (newPassword: string) => {
  AuthServicesLogger.Main.info('Tentando atualizar senha do usuário');

  try {
    const { data, error } = await supabase.auth.updateUser({
      password: newPassword
    });

    if (error) {
      AuthServicesLogger.Main.error('Erro ao atualizar senha:', error);
      return { success: false, error };
    }

    AuthServicesLogger.Main.info('Senha atualizada com sucesso');
    return { success: true, data, error: null };
  } catch (error) {
    AuthServicesLogger.Main.error('Exceção ao atualizar senha:', error);
    return { success: false, error };
  }
}; 
